import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { Megaphone } from "lucide-react";

const MARKETING_ROI = [
  { range: "$0", awareness: "-2 pts/qtr (decay)", load: "-3%" },
  { range: "$100K-$300K", awareness: "+1 to +3 pts", load: "+1-2%" },
  { range: "$300K-$700K", awareness: "+4 to +8 pts", load: "+3-5%" },
  { range: "$700K-$1.5M", awareness: "+9 to +14 pts", load: "+6-9%" },
  { range: "$1.5M+", awareness: "+15 to +20 pts", load: "+10-12%" },
];

export default function MarketingSection({ decision, updateDecision }) {
  const { t } = useTranslation();
  const budget = decision.marketing_budget || 0;
  const mix = decision.marketing_channel_mix || { digital: 50, traditional: 50 };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Budget */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5" /> {t("decisions.marketingBudgetLabel")}</CardTitle>
            <CardDescription>{t("decisions.brandAwareness")}: {decision._brand_awareness || "35"}/100</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <div className="flex justify-between mb-1">
                <Label className="text-xs">{t("decisions.marketingBudgetLabel")}</Label>
                <span className="font-mono text-sky-600 font-semibold text-sm">{formatCurrency(budget)}</span>
              </div>
              <Slider value={[budget]} onValueChange={([v]) => updateDecision("marketing_budget", v)} min={0} max={2000000} step={50000} className="mt-2" data-testid="marketing-budget-slider" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>$0</span><span>$2,000,000</span></div>
            </div>
            <div>
              <div className="flex justify-between mb-1">
                <Label className="text-xs">{t("decisions.digital")} vs {t("decisions.traditional")}</Label>
                <span className="text-xs font-mono">{mix.digital}% / {mix.traditional}%</span>
              </div>
              <Slider value={[mix.digital]} onValueChange={([v]) => updateDecision("marketing_channel_mix", { digital: v, traditional: 100 - v })} min={0} max={100} step={10} className="mt-2" data-testid="channel-mix-slider" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>100% {t("decisions.traditional")}</span><span>100% {t("decisions.digital")}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Loyalty & Campaigns */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.loyaltyCampaigns")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <div>
              <Label className="text-xs">{t("decisions.loyaltyProgram")}</Label>
              <div className="space-y-2 mt-2">
                {[
                  { value: "none", labelKey: "decisions.none", cost: "$0", descKey: "decisions.loyaltyNone" },
                  { value: "basic", labelKey: "decisions.basic", cost: "$300K/qtr", descKey: "decisions.loyaltyBasic" },
                  { value: "premium", labelKey: "decisions.premium", cost: "$700K/qtr", descKey: "decisions.loyaltyPremium" },
                ].map(opt => (
                  <button key={opt.value} onClick={() => updateDecision("loyalty_program", opt.value)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors ${decision.loyalty_program === opt.value ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`loyalty-${opt.value}`}>
                    <div className="flex justify-between"><span className="font-medium text-sm">{t(opt.labelKey)}</span><span className="text-xs font-mono text-slate-500">{opt.cost}</span></div>
                    <p className="text-xs text-slate-500">{t(opt.descKey)}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("decisions.promotionalCampaign")}</Label>
              <Select value={decision.promotional_campaign || "none"} onValueChange={v => updateDecision("promotional_campaign", v)}>
                <SelectTrigger className="mt-1" data-testid="campaign-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("decisions.loyaltyNone")}</SelectItem>
                  <SelectItem value="fare_sale">{t("decisions.campaignFareSale")}</SelectItem>
                  <SelectItem value="launch_offer">{t("decisions.campaignLaunchOffer")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROI Guide */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Marketing ROI Guide</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[400px]">
            <thead><tr className="border-b"><th className="text-left py-1.5 px-2">Budget</th><th className="text-left py-1.5 px-2">Brand Awareness</th><th className="text-left py-1.5 px-2">Load Factor</th></tr></thead>
            <tbody>
              {MARKETING_ROI.map((row, i) => (
                <tr key={i} className="border-b border-slate-50"><td className="py-1.5 px-2 font-mono">{row.range}</td><td className="py-1.5 px-2">{row.awareness}</td><td className="py-1.5 px-2">{row.load}</td></tr>
              ))}
            </tbody>
          </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
